/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useManagerDashboard.ts
 * PURPOSE: TanStack Query hooks for Manager Dashboard — Sprint 75 S75-001
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-03-20
 * COMPLIANCE: SDLC 6.3.1 + Sprint Governance Rule 3 (Zero Mock Tolerance)
 * PILLAR: P2 (Sprint Governance) + P4 (Quality Gates)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs (<50ms p95)
 * SPRINT: S75-001 — TanStack Query hooks replacing useState+useEffect+setTimeout
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management — Manager Decision Support
 * - Impact: Revenue Critical — eliminates CTO Finding #32 (setTimeout mock)
 * - Stakeholders: Branch Managers, Operations Team
 *
 * @technical-architecture
 * - Layer: Custom Hooks (Business Logic)
 * - Pattern: TanStack Query v5 (useQuery, useMutation, useQueryClient)
 * - Dependencies: @tanstack/react-query, managerApi, TypeScript 5.0+
 *
 * @cross-references
 * - API Service: frontend/src/services/api/managerApi.ts
 * - Component: frontend/src/components/dashboards/ManagerDashboard.tsx
 * - Backend: backend/app/api/v2/endpoints/manager_approvals.py
 * - Design: docs/02-design/04-ui-ux-design/SPEC-0407-manager-dashboards-design.md
 * ===============================================================================
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  managerApi,
  type BulkApproveRequest,
  type PendingApprovalsParams,
  type AlertsParams,
} from "../services/api/managerApi";

// =============================================================================
// Query Keys — centralized for cache invalidation
// =============================================================================

export const MANAGER_QUERY_KEYS = {
  kpis: ["manager", "kpis"] as const,
  alerts: (limit?: number) => ["manager", "alerts", limit] as const,
  activeStaff: ["manager", "active-staff"] as const,
  pendingApprovals: (params?: PendingApprovalsParams) =>
    ["manager", "pending-approvals", params] as const,
};

// =============================================================================
// useManagerKPIs — S75-001: replaces hardcoded kpi data in ManagerDashboard
// =============================================================================

export function useManagerKPIs() {
  return useQuery({
    queryKey: MANAGER_QUERY_KEYS.kpis,
    queryFn: () => managerApi.getKPIs(),
    refetchInterval: 60_000, // Refresh every 60s for real-time feel
    staleTime: 30_000,       // Consider fresh for 30s
    retry: 2,
  });
}

// =============================================================================
// useManagerAlerts — S75-001: replaces hardcoded alerts / todayTasks
// =============================================================================

export function useManagerAlerts(params?: AlertsParams) {
  return useQuery({
    queryKey: MANAGER_QUERY_KEYS.alerts(params?.limit),
    queryFn: () => managerApi.getAlerts(params),
    refetchInterval: 120_000, // Refresh every 2 min
    staleTime: 60_000,
    retry: 2,
  });
}

// =============================================================================
// useActiveStaff — S75-001: replaces hardcoded staffMembers array
// =============================================================================

export function useActiveStaff(enabled: boolean = true) {
  return useQuery({
    queryKey: MANAGER_QUERY_KEYS.activeStaff,
    queryFn: () => managerApi.getActiveStaff(),
    refetchInterval: 30_000, // Refresh every 30s (staff check in/out frequently)
    staleTime: 15_000,
    retry: 2,
    enabled, // S102L-083: gate fetch for CORPORATE_MANAGER
  });
}

// =============================================================================
// usePendingApprovals — for ManagerApprovalQueue page
// =============================================================================

export function usePendingApprovals(params?: PendingApprovalsParams) {
  return useQuery({
    queryKey: MANAGER_QUERY_KEYS.pendingApprovals(params),
    queryFn: () => managerApi.getPendingApprovals(params),
    staleTime: 30_000,
    retry: 2,
  });
}

// =============================================================================
// useBulkApprove — S75-007: mutation for approve/reject with cache invalidation
// =============================================================================

export function useBulkApprove() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: BulkApproveRequest) => managerApi.bulkApprove(payload),
    onSuccess: () => {
      // Invalidate all manager caches after bulk action
      queryClient.invalidateQueries({ queryKey: ["manager"] });
    },
  });
}
